import React, { useEffect, useState } from 'react';
import {
  Grid,
  Paper,
  Button,
  Container,
  TextField,
  Typography,
  Box,
  Card,
  CardContent,
  useTheme,
  useMediaQuery
} from '@material-ui/core';
import {
  Add as AddIcon,
  AttachMoney,
  Business
} from '@material-ui/icons';
import { makeStyles } from "@material-ui/core/styles";
import { toast } from "react-toastify";
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';

import PendingCommandes from '../PendingCommandes';
import AddCommande from '../AddCommande';
import NumCommandeService from '../../../services/NumCommandeService';
import BonService from '../../../services/BonService';

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: '#f8f9fa',
    minHeight: '100vh',
    padding: theme.spacing(3),
  },
  header: {
    background: 'linear-gradient(135deg, #226D68 0%, #2a857e 100%)',
    color: 'white',
    borderRadius: '16px',
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
    boxShadow: '0 8px 32px rgba(34, 109, 104, 0.2)',
  },
  title: {
    fontWeight: 700,
    fontSize: '2rem',
    textAlign: 'center',
    marginBottom: theme.spacing(1),
    background: 'linear-gradient(135deg, #ffffff 0%, #f0f0f0 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
  },
  statsContainer: {
    marginBottom: theme.spacing(3),
  },
  statCard: {
    background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
    borderRadius: '12px',
    padding: theme.spacing(2),
    textAlign: 'center',
    transition: 'all 0.3s ease',
    border: '1px solid #e0e0e0',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 6px 20px rgba(34, 109, 104, 0.15)',
    },
  },
  statIcon: {
    width: 40,
    height: 40,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 8px',
    background: 'linear-gradient(135deg, #226D68 0%, #2a857e 100%)',
    color: 'white',
    fontSize: 18,
  },
  statNumber: {
    fontWeight: 700,
    color: '#226D68',
    fontSize: '1.25rem',
    marginBottom: theme.spacing(0.5),
  },
  statLabel: {
    color: '#666',
    fontWeight: 600,
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  formContainer: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
    border: '1px solid #e0e0e0',
  },
  addButton: {
    background: 'linear-gradient(135deg, #f26522 0%, #ff8c42 100%)',
    color: 'white',
    borderRadius: '8px',
    padding: theme.spacing(1, 3),
    fontWeight: 600,
    textTransform: 'none',
    fontSize: '16px',
    '&:hover': {
      background: 'linear-gradient(135deg, #e55a1a 0%, #ff7b2e 100%)',
      transform: 'translateY(-1px)',
    },
  },
  leftPanel: {
    height: 'fit-content',
    position: 'sticky',
    top: theme.spacing(2),
  },
}));

const AjoutCommandes = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [commandes, setCommandes] = useState([]);
  const [total, setTotal] = useState(0);
  const [nums, setNums] = useState([]);

  // Chargement des données
  const refreshCommandes = async () => {
    try {
      const response = await BonService.getBonsEncours();
      setCommandes(response.data);
    } catch (error) {
      console.error('Erreur chargement commandes:', error);
      toast.error('Erreur lors du chargement des commandes');
    }
  };

  const fetchNums = async () => {
    try {
      const response = await NumCommandeService.getNumbers();
      setNums(response.data);
    } catch (error) {
      console.error('Erreur chargement numéros:', error);
    }
  };

  // Calcul du total
  const calculateTotal = () => {
    const sum = commandes.reduce((acc, item) => {
      const qte = parseFloat(item.quan_livree) || 0;
      const prix = parseFloat(item.prixUnitaire) || 0;
      return acc + (qte * prix);
    }, 0);
    setTotal(sum);
  };

    // Fonction appelée quand une commande est modifiée
    const handleCommandeUpdate = () => {
      // Rafraîchir les commandes puis recalculer
      refreshCommandes();
    };

  // Effets
  useEffect(() => {
    refreshCommandes();
    fetchNums();
  }, []);

  useEffect(() => {
    calculateTotal();
  }, [commandes]);

  // Formulaire d'ajout de numéro
  const initialNumbers = { numero: '' };
  
  const validationSchemaCommande = Yup.object().shape({
    numero: Yup.string().required('Le numéro est obligatoire')
  });

  const onSubmitVente = async (values, { resetForm }) => {
    try {
      await NumCommandeService.createNum(values);
      await fetchNums();
      toast.success('Numéro ajouté avec succès', {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
      resetForm();
    } catch (error) {
      console.error('Erreur ajout numéro:', error);
      toast.error("Erreur lors de l'ajout du numéro");
    }
  };

  // Composant de formulaire
  const NumeroForm = () => (
    <Paper className={classes.formContainer}>
      <Typography variant="h6" gutterBottom style={{ color: '#226D68', fontWeight: 600 }}>
        Ajouter un numéro de commande
      </Typography>
      <Formik 
        initialValues={initialNumbers} 
        validationSchema={validationSchemaCommande} 
        onSubmit={onSubmitVente}
      >
        {({ isSubmitting }) => (
          <Form>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={8}>
                <Field 
                  as={TextField}
                  name="numero"
                  label="Numéro de commande"
                  variant="outlined"
                  fullWidth
                  size="small"
                  helperText={<ErrorMessage name="numero" />}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Button
                  type="submit"
                  className={classes.addButton}
                  variant="contained"
                  fullWidth
                  disabled={isSubmitting}
                  startIcon={<AddIcon />}
                >
                  Ajouter
                </Button>
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
    </Paper>
  );

  // Composant de statistiques
  const StatsCards = () => (
    <Box className={classes.statsContainer}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={4}>
          <Card className={classes.statCard}>
            <CardContent>
              <div className={classes.statIcon}>
                <AttachMoney />
              </div>
              <Typography className={classes.statNumber}>
                {total.toLocaleString('fr-FR')} Fcfa
              </Typography>
              <Typography className={classes.statLabel}>
                Total Commandes
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card className={classes.statCard}>
            <CardContent>
              <div className={classes.statIcon}>
                <Business />
              </div>
              <Typography className={classes.statNumber}>
                {commandes.length}
              </Typography>
              <Typography className={classes.statLabel}>
                Produits
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card className={classes.statCard}>
            <CardContent>
              <div className={classes.statIcon} style={{ background: 'linear-gradient(135deg, #f26522 0%, #ff8c42 100%)' }}>
                <AddIcon />
              </div>
              <Typography className={classes.statNumber}>
                {nums.length}
              </Typography>
              <Typography className={classes.statLabel}>
                Numéros Disponibles
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );

  return (
    <div className={classes.root}>
      <Container maxWidth="xl">
        {/* Formulaire d'ajout de numéro */}
        <NumeroForm />

        {/* Cartes de statistiques */}
        <StatsCards />

        {/* Contenu principal */}
        <Grid container spacing={3}>
          {/* Panneau gauche - Ajout de commande */}
          <Grid item xs={12} md={4} className={classes.leftPanel}>
            <Paper 
              elevation={2} 
              sx={{ 
                borderRadius: '12px',
                overflow: 'hidden',
                border: '1px solid #e0e0e0'
              }}
            >
              <AddCommande 
                nums={nums} 
                refresh={refreshCommandes} 
              />
            </Paper>
          </Grid>

          {/* Panneau droit - Liste des commandes */}
          <Grid item xs={12} md={8}>
            <Paper 
              elevation={2} 
              sx={{ 
                borderRadius: '12px',
                overflow: 'hidden',
                border: '1px solid #e0e0e0'
              }}
            >
              <PendingCommandes 
                commandes={commandes} 
                refresh={refreshCommandes} 
                onUpdate={handleCommandeUpdate}
              />
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </div>
  );
};

export default AjoutCommandes;